import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard, Activity, DollarSign, Zap, MessageSquare,
  ChevronLeft, ChevronRight, Bell, Settings, AlertTriangle, LogOut, Leaf, Building2, Database, Cloud, Mail, ShieldCheck,
} from 'lucide-react';
import { useStore } from '../../store';
import { alertsApi, tenantsApi } from '../../services/api';
import NotificationBell from '../NotificationBell';
import { useRole } from '../../hooks/useRole';

const NAV_ITEMS = [
  { to: '/dashboard',    icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/monitoring',   icon: Activity,        label: 'Monitoring',  staffOnly: true },
  { to: '/cost',         icon: DollarSign,      label: 'Cost Reports' },
  { to: '/optimization', icon: Zap,             label: 'Optimization' },
  { to: '/db-optimization', icon: Database,     label: 'DB Optimization', staffOnly: true },
  { to: '/sustainability', icon: Leaf,          label: 'Sustainability' },
  { to: '/ai',           icon: MessageSquare,   label: 'AI Assistant', staffOnly: true },
  { to: '/alerts',       icon: AlertTriangle,   label: 'Alerts',      hasBadge: true, staffOnly: true },
  { type: 'separator',   staffOnly: true },
  { to: '/cloud-connections',       icon: Cloud,       label: 'Cloud Connections', staffOnly: true },
  { to: '/notifications-settings',  icon: Mail,        label: 'Notifications' },
  { to: '/audit',                   icon: ShieldCheck, label: 'Audit & Compliance', staffOnly: true },
  { to: '/settings',     icon: Settings,        label: 'Settings',    staffOnly: true },
];

const ENVS = ['production', 'staging', 'dev'];

export default function Layout() {
  const { sidebarOpen, toggleSidebar, user, logout, tenants, activeTenantId, setTenants, setActiveTenant } = useStore();
  const [activeEnv, setActiveEnv] = React.useState('production');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isStaffOrAbove } = useRole();
  const visibleNav = NAV_ITEMS.filter((item) => !item.staffOnly || isStaffOrAbove);

  const { data: alertCountData } = useQuery({
    queryKey: ['alert-count'],
    queryFn: () => alertsApi.getCount().then((r) => r.data).catch(() => null),
    refetchInterval: 30000,
    retry: false,
  });

  // Fetch tenants
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants-list'],
    queryFn: () => tenantsApi.getAll().then((r) => r.data).catch(() => []),
    refetchInterval: 60000,
    retry: false,
  });

  React.useEffect(() => {
    if (Array.isArray(tenantsData) && tenantsData.length > 0) {
      setTenants(tenantsData);
      if (!activeTenantId) {
        setActiveTenant(tenantsData[0].id);
      }
    }
  }, [tenantsData, activeTenantId, setTenants, setActiveTenant]);

  const alertCount = alertCountData?.count ?? alertCountData?.unacknowledged ?? 5;

  const handleTenantChange = (tenantId) => {
    if (tenantId === activeTenantId) return;
    // Update localStorage FIRST so the API interceptor picks up the new tenant
    localStorage.setItem('smalblu_active_tenant', tenantId);
    setActiveTenant(tenantId);
    // Clear all cached query data - forces all pages to refetch with new tenant header
    queryClient.clear();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const activeTenant = tenants.find((t) => t.id === activeTenantId);

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      background: '#0a0f1e',
      fontFamily: '"DM Sans", system-ui, sans-serif',
    }}>
      {/* ── Sidebar ─────────────────────────────────────── */}
      <aside style={{
        width: sidebarOpen ? 240 : 68,
        background: 'linear-gradient(180deg, #0d1527 0%, #0a0f1e 100%)',
        borderRight: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        flexShrink: 0,
        position: 'relative',
      }}>
        {/* Logo */}
        <div style={{
          padding: '24px 16px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <div style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            overflow: 'hidden',
            flexShrink: 0,
            boxShadow: '0 4px 16px rgba(0,132,255,0.25)',
          }}>
            <img
              src="https://framerusercontent.com/images/whCtlttISdJDksSMYxc5sAgsp1A.jpg"
              alt="SmalBlu"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          {sidebarOpen && (
            <div style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
              <div style={{
                color: '#fff',
                fontWeight: 700,
                fontSize: 18,
                letterSpacing: '-0.5px',
                lineHeight: 1.2,
              }}>SmalBlu</div>
              {/*<div style={{
                color: '#4a9eff',
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}>Cloud Optimizer</div>*/}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '8px 10px' }}>
          {visibleNav.map((item, idx) => {
            if (item.type === 'separator') {
              return (
                <div key={`sep-${idx}`} style={{
                  height: 1,
                  background: 'rgba(255,255,255,0.06)',
                  margin: '8px 12px',
                }} />
              );
            }
            const { to, icon: Icon, label, hasBadge } = item;
            return (
              <NavLink
                key={to}
                to={to}
                style={({ isActive }) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '11px 12px',
                  borderRadius: 8,
                  marginBottom: 4,
                  textDecoration: 'none',
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.45)',
                  background: isActive ? 'rgba(0,132,255,0.12)' : 'transparent',
                  borderLeft: isActive ? '3px solid #0084ff' : '3px solid transparent',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  position: 'relative',
                })}
              >
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <Icon size={18} style={{ flexShrink: 0 }} />
                  {hasBadge && alertCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: -6,
                      right: -8,
                      minWidth: 16,
                      height: 16,
                      borderRadius: 8,
                      background: '#ff5c5c',
                      color: '#fff',
                      fontSize: 9,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px',
                      lineHeight: 1,
                    }}>
                      {alertCount > 99 ? '99+' : alertCount}
                    </span>
                  )}
                </div>
                {sidebarOpen && (
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{label}</span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        {sidebarOpen && (
          <div style={{
            padding: '12px 16px 20px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
          }}>
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>
              <div style={{ marginBottom: 2 }}>Version 0.3.0</div>
              <div style={{ marginBottom: 8 }}>© 2024 SmalBlu</div>
              <a href="mailto:hello@smalblu.ai" style={{ color: 'rgba(74,158,255,0.7)', display: 'block', textDecoration: 'none', marginBottom: 4 }}>hello@smalblu.ai</a>
              <a href="https://www.linkedin.com/company/smalblu/" target="_blank" rel="noreferrer" style={{ color: 'rgba(74,158,255,0.7)', display: 'block', textDecoration: 'none', marginBottom: 4 }}>LinkedIn</a>
              <a href="https://calendly.com/garv-smalblu" target="_blank" rel="noreferrer" style={{ color: 'rgba(74,158,255,0.7)', display: 'block', textDecoration: 'none' }}>Book a Demo</a>
            </div>
          </div>
        )}

        {/* Collapse toggle */}
        <button
          onClick={toggleSidebar}
          style={{
            position: 'absolute',
            right: -13,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 26,
            height: 26,
            borderRadius: '50%',
            background: '#1a2540',
            border: '1px solid rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.6)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            transition: 'background 0.15s',
          }}
        >
          {sidebarOpen ? <ChevronLeft size={13} /> : <ChevronRight size={13} />}
        </button>
      </aside>

      {/* ── Main Area ──────────────────────────────────── */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Top Bar */}
        <header style={{
          height: 60,
          background: 'rgba(13,21,39,0.92)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '0 24px',
          gap: 8,
          backdropFilter: 'blur(16px)',
          flexShrink: 0,
        }}>
          {/* Tenant Selector */}
          {isStaffOrAbove && tenants.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 8 }}>
              <Building2 size={14} color="rgba(255,255,255,0.35)" />
              <select
                value={activeTenantId || ''}
                onChange={(e) => handleTenantChange(e.target.value)}
                style={{
                  background: 'rgba(255,153,0,0.08)',
                  border: '1px solid rgba(255,153,0,0.25)',
                  color: '#ff9900',
                  borderRadius: 8,
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  minWidth: 140,
                }}
              >
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Divider */}
          <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.08)', margin: '0 4px' }} />

          {/* Environment Selector */}
          {ENVS.map((env) => (
            <button
              key={env}
              onClick={() => setActiveEnv(env)}
              style={{
                padding: '5px 14px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 500,
                border: `1px solid ${activeEnv === env ? 'rgba(0,132,255,0.4)' : 'rgba(255,255,255,0.1)'}`,
                background: activeEnv === env ? 'rgba(0,132,255,0.15)' : 'transparent',
                color: activeEnv === env ? '#4a9eff' : 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                textTransform: 'capitalize',
                transition: 'all 0.15s',
              }}
            >
              {env}
            </button>
          ))}

          {/* Divider */}
          <div style={{
            width: 1,
            height: 24,
            background: 'rgba(255,255,255,0.08)',
            margin: '0 8px',
          }} />

          {/* Notification inbox popover */}
          <NotificationBell />

          {/* Alerts (legacy — infrastructure alerts, separate from notifications) */}
          <button
            onClick={() => navigate('/alerts')}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              padding: 6,
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color 0.15s',
              position: 'relative',
            }}
          >
            <Bell size={18} />
            {alertCount > 0 && (
              <span style={{
                position: 'absolute',
                top: 0,
                right: 0,
                minWidth: 16,
                height: 16,
                borderRadius: 8,
                background: '#ff5c5c',
                color: '#fff',
                fontSize: 9,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
                lineHeight: 1,
              }}>
                {alertCount > 99 ? '99+' : alertCount}
              </span>
            )}
          </button>

          {/* Settings */}
          {isStaffOrAbove && (
          <button
            onClick={() => navigate('/settings')}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              padding: 6,
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color 0.15s',
            }}
          >
            <Settings size={18} />
          </button>
          )}

          {/* User info & Avatar */}
          {user && sidebarOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: 4, marginRight: 4 }}>
              <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 600 }}>{user.name || 'User'}</span>
              <span style={{ color: '#4a9eff', fontSize: 10, fontWeight: 500 }}>{user.role || 'Viewer'}</span>
            </div>
          )}
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name || 'User'}
              referrerPolicy="no-referrer"
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                objectFit: 'cover',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,132,255,0.2)',
              }}
            />
          ) : (
            <div style={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #00d4ff, #0066ff)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,132,255,0.2)',
            }}>
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          )}

          {user && (
            <button
              onClick={handleLogout}
              title="Logout"
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                padding: 6,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LogOut size={16} />
            </button>
          )}
        </header>

        {/* Page Content */}
        <main style={{
          flex: 1,
          overflow: 'auto',
          padding: 28,
        }}>
          <Outlet key={activeTenantId || 'default'} />
        </main>
      </div>
    </div>
  );
}
